/* eslint-disable react/prop-types */
import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import OrionButton from "../../button";
import ActionButtons from "../../actionButtons";
import ConfirmationModal from "../../modal/confirmationModal";
import Dropdown from "../../dropdown";

import { useTable } from "../context";
import { isFunction } from "../utils";

const TableAction = ({ actionType, ...props }) => {
  const [isOpen, setOpen] = useState(false);
  const openModal = () => setOpen(true);
  const closeModal = () => setOpen(false);
  const {
    selectedRows,
    selectedRow,
    data,
    uniqueDataKey,
    multiSelect,
    paginationEnabled
  } = useTable();
  let currentRows = selectedRows;
  if (paginationEnabled) {
    const allRowIds = data ? data.map(obj => obj[uniqueDataKey]) : [];
    currentRows = selectedRows.filter(rowId => allRowIds.includes(rowId));
  }
  const selectedItems = useMemo(
    () => data.filter(item => selectedRows.includes(item[uniqueDataKey])),
    [selectedRows, data, uniqueDataKey]
  );
  const selectedRowItem = useMemo(
    () =>
      !multiSelect
        ? data.find(item => item[uniqueDataKey] === selectedRow)
        : null,
    [selectedRow, data, uniqueDataKey, multiSelect]
  );
  const itemSelectHandler = item => {
    if (multiSelect) {
      props.onItemClick(item, selectedRows, selectedItems);
    } else {
      props.onItemClick(item, selectedRow, selectedRowItem);
    }
  };
  if (actionType === "button") {
    return (
      <OrionButton
        {...props}
        disabled={
          (currentRows.length === 0 && selectedRow === null) || props.disabled
        }
        onClick={
          isFunction(props.onClick)
            ? () =>
                multiSelect
                  ? props.onClick(currentRows, selectedItems)
                  : props.onClick(
                      selectedRow,
                      selectedRowItem
                    ) /* istanbul ignore next */
            : null
        }
      />
    );
  }

  if (actionType === "actionButton") {
    const buttons = props.buttons.map(btn => ({
      ...btn,
      disabled:
        (currentRows.length === 0 && selectedRow === null) || btn.disabled,
      onClick: () => {
        /* istanbul ignore else */
        if (isFunction(btn.onClick)) {
          if (multiSelect) {
            btn.onClick(currentRows, selectedItems);
          } else {
            btn.onClick(selectedRow, selectedRowItem);
          }
        }
      }
    }));

    return (
      <ActionButtons
        {...props}
        buttons={buttons}
        disableMoreBtn={
          (currentRows.length === 0 && selectedRow === null) ||
          props.disableMoreBtn
        }
      />
    );
  }

  /* istanbul ignore else */
  if (actionType === "confirmButton") {
    return (
      // eslint-disable-next-line react/jsx-fragments
      <React.Fragment>
        <ConfirmationModal
          {...props}
          size={props.modalSize}
          isOpen={isOpen}
          toggle={closeModal}
          onContinue={() => {
            /* istanbul ignore else */
            if (isFunction(props.onContinue)) {
              let result = Promise.resolve(true);
              if (multiSelect) {
                result = props.onContinue(currentRows, selectedItems);
              } else {
                result = props.onContinue(selectedRow, selectedRowItem);
              }

              /* istanbul ignore else */
              if (result instanceof Promise) {
                result.then(() => closeModal());
              }
            }
          }}
        />
        <Button
          {...props}
          disabled={
            (currentRows.length === 0 && selectedRow === null) || props.disabled
          }
          onClick={openModal}
        />
      </React.Fragment>
    );
  }

  if (actionType === "dropDownButton") {
    return (
      <Dropdown
        className={props.className}
        items={props.items}
        disabled={
          (currentRows.length === 0 && selectedRow === null) || props.disabled
        }
        label={props.label}
        type={props.type}
        onItemClick={itemSelectHandler}
      />
    );
  }
  return null;
};

TableAction.defaultProps = {
  modalSize: "md"
};

TableAction.propTypes = {
  modalSize: PropTypes.string,
  actionType: PropTypes.string,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  onContinue: PropTypes.func,
  disableMoreBtn: PropTypes.bool,
  buttons: PropTypes.array
};

const Button = props => <TableAction {...props} actionType="button" />;

const ConfirmButton = props => (
  <TableAction {...props} actionType="confirmButton" />
);

const ActionButton = props => (
  <TableAction {...props} actionType="actionButton" />
);

const DropdownButton = props => (
  <TableAction {...props} actionType="dropDownButton" />
);

export { Button, ConfirmButton, ActionButton, DropdownButton };
